import { useEffect, useState, useMemo } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { ScoreBadge, PriorityPill } from "@/components/app/ScoreBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Filter, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { InfoTip } from "@/components/app/InfoTip";

const STATUSES = ["Open", "In Progress", "Completed", "Rejected", "On Hold"];

export default function Bidding() {
  const { selectedBrandId } = useBrands();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [signal, setSignal] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (!selectedBrandId) return;
    setLoading(true);
    const params = new URLSearchParams({ brand_id: selectedBrandId, limit: "500" });
    if (filter !== "all") params.set("priority", filter);
    if (signal !== "all") params.set("signal", signal);
    api.get(`/bidding?${params}`).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, [selectedBrandId, filter, signal]);

  const regenerate = async () => {
    try {
      await api.post(`/recommendations/regenerate?brand_id=${selectedBrandId}`);
      toast.success("Recommendations regenerated"); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bidding/${id}`, { status });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) { toast.error(extractError(e)); }
  };

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((i) => (i.keyword || "").toLowerCase().includes(q) || (i.product_name || "").toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Execution</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Bidding Recommendations</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} actions · suggested bids in INR · accept / reject directly from the table.</p>
        </div>
        <Button data-testid="bidding-regenerate" variant="outline" onClick={regenerate} className="gap-2">
          <RefreshCw className="h-3.5 w-3.5" /> Regenerate
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="bidding-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search keyword or product…" className="pl-9 h-9" />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            {[["all", "All"], ["High", "High"], ["Medium", "Medium"], ["Low", "Low"]].map(([k, l]) => (
              <button key={k} data-testid={`bidding-filter-${k}`} onClick={() => setFilter(k)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 ml-2 pl-2 border-l border-slate-200">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1">
              Closed-loop <InfoTip term="CLOSED_LOOP" />
            </span>
            {[["all", "All"], ["auto_negative", "Auto-neg"], ["boost", "Boost"], ["monitor", "Monitor"]].map(([k, l]) => (
              <button key={k} data-testid={`bidding-signal-${k}`} onClick={() => setSignal(k)}
                className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  signal === k
                    ? (k === "auto_negative" ? "bg-rose-600 text-white border-rose-600"
                      : k === "boost" ? "bg-emerald-600 text-white border-emerald-600"
                      : k === "monitor" ? "bg-slate-700 text-white border-slate-700"
                      : "bg-slate-900 text-white border-slate-900")
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto" data-testid="bidding-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left">Priority</th>
                <th className="px-4 py-3 text-left"><span className="inline-flex items-center gap-1">Match <InfoTip term="EXACT_MATCH" title="Match type" text="Exact = exact term only · Phrase = ordered words · Broad = related searches · Negative = excluded queries." /></span></th>
                <th className="px-4 py-3 text-left">Product / Keyword</th>
                <th className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 justify-end">Bid Range <InfoTip term="CPC" title="Suggested bid range (INR)" text="Recommended Cost-Per-Click range. Higher tiers reflect demand × business fit." /></span></th>
                <th className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 justify-end">Opportunity <InfoTip term="OPPORTUNITY" /></span></th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3 text-left">Reason</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3"><PriorityPill priority={b.priority} /></td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{b.match_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 inline-flex items-center gap-1.5">
                      {b.keyword}
                      {b.learning_signal === "auto_negative" && (
                        <span data-testid={`signal-neg-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700">
                          Auto-neg <InfoTip term="AUTO_NEGATIVE" className="text-rose-500 hover:text-rose-800" />
                        </span>
                      )}
                      {b.learning_signal === "boost" && (
                        <span data-testid={`signal-boost-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                          Boost <InfoTip term="BID_BOOST" className="text-emerald-500 hover:text-emerald-800" />
                        </span>
                      )}
                      {b.learning_signal === "monitor" && (
                        <span data-testid={`signal-monitor-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600">
                          Monitor <InfoTip term="MONITOR_SIGNAL" className="text-slate-500 hover:text-slate-900" />
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{b.product_name || <span className="text-slate-400">— no SKU match —</span>}</div>
                    {(b.last_week_spend != null || b.last_week_orders != null) && (
                      <div className="mt-0.5 text-[10px] text-slate-500 font-mono">
                        last wk: ₹{Math.round(b.last_week_spend || 0)} · {b.last_week_orders || 0} ord{b.last_week_acos != null ? ` · ${b.last_week_acos}% ACoS` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-slate-900">
                    {b.suggested_bid_low === 0 && b.suggested_bid_high === 0 ? <span className="text-slate-400">—</span> : <>₹{b.suggested_bid_low}–{b.suggested_bid_high}</>}
                  </td>
                  <td className="px-4 py-3 text-right"><ScoreBadge score={b.opportunity_score} /></td>
                  <td className="px-4 py-3 text-xs text-slate-700">{b.action}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-[220px]">{b.reason}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`bidding-status-${b.id}`} value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)} className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {!filtered.length && !loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">
                  {!items.length
                    ? "No bidding recommendations yet. Upload a CSV first."
                    : "No actions match your filter — try widening it."}
                </td></tr>
              )}
              {loading && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {items.length > 0 && items.every((b) => b.suggested_bid_low === 0 && b.suggested_bid_high === 0) && (
        <div data-testid="bidding-empty-bids-warning" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <b>All recommended actions are flagged as Negative or Hold.</b> This usually means the uploaded CSV's keywords don't match the brand's product catalogue.
          Add more relevant SKUs in <a href="/products" className="underline font-semibold">Products</a> or upload a CSV that matches your category, then click <b>Regenerate</b>.
        </div>
      )}
    </div>
  );
}
